import { supabase } from '@core/supabase/client';
import { applicationService } from '@core/supabase/services/applicationService';
import type { MatchResult } from '../types';

type UserRef = { orgId: string; id: string };

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value);
}

/**
 * Returns an application id for this candidate–job pair, creating a pipeline
 * row when we have enough context to do so (real candidate + real job +
 * resolvable client). If any of those aren't available — which is the common
 * case for **Sourcing** results backed by synthetic/thread IDs rather than a
 * real pipeline job — we return `{ applicationId: null, created: false,
 * skipped: true }` so the caller can still treat the shortlist as a local /
 * collection-only action without crashing with a 400.
 *
 * NOTE (21-Apr): the previous implementation always INSERTed with
 * `client_id: (m.job?.client_id ?? '')`. Passing an empty string into a UUID
 * column triggers a 400 from PostgREST which is the error the user hit when
 * shortlisting a Sourcing result. We now:
 *   1. Id-lookup only (no embeds) so we survive half-migrated projects
 *      missing `058_ggj_b2b_application_pipeline_lock.sql`.
 *   2. Resolve `client_id` from the jobs table when the match doesn't carry
 *      one (typical for raw sourcing results).
 *   3. Skip the insert entirely when the ids aren't real UUIDs (e.g.
 *      `linkedin:xyz`) — sourcing threads don't map 1:1 to pipeline rows,
 *      so the UI keeps the shortlist in its client-side set.
 */
export async function ensureApplicationForMatch(
  user: UserRef,
  m: MatchResult
): Promise<{ applicationId: string | null; created: boolean; skipped?: boolean }> {
  const candidateId = m.candidate_id;
  const jobId = m.job_id;

  if (!isUuid(candidateId) || !isUuid(jobId)) {
    // Sourcing results frequently carry synthetic ids (thread-based or
    // external-source-backed). We can't build a real pipeline row for
    // those, so surface that to the caller.
    return { applicationId: null, created: false, skipped: true };
  }

  // 1) Look for an existing application with a minimal select — no embeds
  //    so this works even without migration 058.
  const { data: existing, error: findErr } = await supabase
    .from('ggj_b2b_applications')
    .select('id')
    .eq('org_id', user.orgId)
    .eq('candidate_id', candidateId)
    .eq('job_id', jobId)
    .limit(1)
    .maybeSingle();
  if (findErr) {
    throw findErr;
  }
  if (existing?.id) {
    return { applicationId: existing.id, created: false };
  }

  // 2) Resolve client_id. Match rows may omit it (e.g. sourcing), so fall
  //    back to the jobs table. If we still can't find one, skip — client_id
  //    is NOT NULL on ggj_b2b_applications.
  let clientId: string | null =
    (m.job as unknown as { client_id?: string })?.client_id ?? null;
  if (!isUuid(clientId)) {
    const { data: jobRow, error: jobErr } = await supabase
      .from('ggj_b2b_jobs')
      .select('client_id')
      .eq('id', jobId)
      .limit(1)
      .maybeSingle();
    if (jobErr) {
      throw jobErr;
    }
    const resolved = jobRow?.client_id as unknown;
    clientId = isUuid(resolved) ? (resolved as string) : null;
  }
  if (!isUuid(clientId)) {
    // No real job/client → treat as a local (collections-only) shortlist.
    return { applicationId: null, created: false, skipped: true };
  }

  // 3) Create the pipeline row with a slim select that only returns `id`.
  //    We intentionally bypass `applicationService.create` (which pulls in
  //    the full embed-heavy select) to avoid 400s on optional
  //    relationships when the full row isn't needed here.
  const insertRow: Record<string, unknown> = {
    org_id: user.orgId,
    candidate_id: candidateId,
    job_id: jobId,
    client_id: clientId,
    created_by: user.id,
    status: 'submitted',
    fitment_score: m.composite_score ?? null,
    fitment_json: {
      source: 'smart_match',
      version: m.match_version ?? 1,
      dimension_scores: m.dimension_scores,
      matched_skills: m.matched_skills,
      missing_skills: m.missing_skills,
    },
  };
  const { data: inserted, error: insertErr } = await supabase
    .from('ggj_b2b_applications')
    .insert(insertRow)
    .select('id')
    .single();
  if (insertErr) throw insertErr;
  void applicationService;
  return { applicationId: inserted!.id, created: true };
}
